import { MultiSelect } from '@/components/multi-select'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { listAttendantsQuery } from '@/queries/attendant.query'
import { listDepartmentsQuery } from '@/queries/department.query'
import { listTagsQuery } from '@/queries/tags.query'
import { IconInfoCircle, IconTrash } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import type { StageAction } from '../../schema'

interface StageActionsProps {
  actions: StageAction[]
  onChange: (actions: StageAction[]) => void
}

const ACTION_LABELS = {
  finalizar_conversa: 'Finalizar Conversa',
  transferir_atendimento: 'Transferir Atendimento',
  aplicar_tags: 'Aplicar Segmentos',
} as const

export function StageActions({ actions, onChange }: StageActionsProps) {
  const { data: attendantsData, isLoading: isLoadingAttendants } =
    useQuery(listAttendantsQuery)
  const { data: departmentsData, isLoading: isLoadingDepartments } =
    useQuery(listDepartmentsQuery)
  const { data: tagsData, isLoading: isLoadingTags } = useQuery(listTagsQuery)

  const generateActionId = (type: string): string => {
    const existingIds = actions.filter((a) => a.type === type).map((a) => a.id)

    let counter = 1
    let newId = `${type}-${counter}`

    while (existingIds.includes(newId)) {
      counter++
      newId = `${type}-${counter}`
    }

    return newId
  }

  const ACTION_BUILDERS: Record<StageAction['type'], () => StageAction> = {
    finalizar_conversa: () => ({
      type: 'finalizar_conversa',
      id: generateActionId('finalizar_conversa'),
    }),
    transferir_atendimento: () => ({
      type: 'transferir_atendimento',
      id: generateActionId('transferir_atendimento'),
      payload: {
        targetType: 'attendant',
        targetId: '',
        targetName: '',
      },
    }),
    aplicar_tags: () => ({
      type: 'aplicar_tags',
      id: generateActionId('aplicar_tags'),
      payload: {
        tagIds: [],
      },
    }),
  }

  const addAction = (type: StageAction['type']) => {
    const builder = ACTION_BUILDERS[type]
    if (!builder) return

    const newAction = builder()
    onChange([...actions, newAction])
  }

  const updateAction = (id: string, updates: Partial<StageAction>) => {
    onChange(
      actions.map((action) =>
        action.id === id ? ({ ...action, ...updates } as StageAction) : action
      )
    )
  }

  const removeAction = (id: string) => {
    onChange(actions.filter((action) => action.id !== id))
  }

  return (
    <div className='space-y-3'>
      <div className='flex items-center justify-between'>
        <Label>Ações do Estágio</Label>
        <Select
          value=''
          onValueChange={(value) => addAction(value as StageAction['type'])}
        >
          <SelectTrigger className='w-[240px]'>
            <SelectValue placeholder='Adicionar ação...' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='finalizar_conversa'>
              {ACTION_LABELS.finalizar_conversa}
            </SelectItem>
            <SelectItem value='transferir_atendimento'>
              {ACTION_LABELS.transferir_atendimento}
            </SelectItem>
            <SelectItem value='aplicar_tags'>
              {ACTION_LABELS.aplicar_tags}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {actions.length === 0 ? (
        <p className='text-sm text-muted-foreground'>
          Nenhuma ação configurada para este estágio.
        </p>
      ) : (
        <>
          {actions.map((action, index) => (
            <Card key={action.id} className='px-4 py-4'>
              <>
                <div className='flex items-center justify-between'>
                  <span className='text-sm font-medium'>
                    {index + 1}. {ACTION_LABELS[action.type]}
                  </span>
                  <Button
                    type='button'
                    variant='ghost'
                    size='icon'
                    onClick={() => removeAction(action.id)}
                  >
                    <IconTrash className='w-4 h-4 text-destructive' />
                  </Button>
                </div>

                {action.type === 'finalizar_conversa' && (
                  <Alert>
                    <IconInfoCircle className='w-4 h-4' />
                    <AlertDescription>
                      Esta ação encerrará automaticamente a conversa quando o
                      agente atingir este estágio. O ticket será marcado como
                      finalizado e o cliente será notificado do encerramento.
                    </AlertDescription>
                  </Alert>
                )}

                {action.type === 'transferir_atendimento' && (
                  <div className='space-y-2'>
                    <div className='space-y-1'>
                      <Label className='text-xs'>Transferir para</Label>
                      <Select
                        value={action.payload.targetType || 'attendant'}
                        onValueChange={(value: 'attendant' | 'department') =>
                          updateAction(action.id, {
                            payload: {
                              ...action.payload,
                              targetType: value,
                              targetId: '',
                              targetName: '',
                            },
                          })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent position='popper' sideOffset={5}>
                          <SelectItem value='attendant'>Atendente</SelectItem>
                          <SelectItem value='department'>
                            Departamento
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className='space-y-1'>
                      <Label className='text-xs'>
                        {action.payload.targetType === 'attendant'
                          ? 'Atendente'
                          : 'Departamento'}
                      </Label>
                      {action.payload.targetType === 'attendant' ? (
                        <Select
                          value={action.payload.targetId || ''}
                          onValueChange={(value) => {
                            const attendant = attendantsData?.attendants?.find(
                              (a) => a.userId === value
                            )
                            updateAction(action.id, {
                              payload: {
                                ...action.payload,
                                targetId: value,
                                targetName: attendant?.user?.name || '',
                              },
                            })
                          }}
                          disabled={isLoadingAttendants}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder='Selecione um atendente' />
                          </SelectTrigger>
                          <SelectContent position='popper' sideOffset={5}>
                            {attendantsData?.attendants?.map((attendant) => (
                              <SelectItem
                                key={attendant.id}
                                value={attendant.userId}
                              >
                                {attendant.user.name}
                              </SelectItem>
                            )) || null}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Select
                          value={action.payload.targetId || ''}
                          onValueChange={(value) => {
                            const department = departmentsData?.find(
                              (d) => d.id.toString() === value
                            )
                            updateAction(action.id, {
                              payload: {
                                ...action.payload,
                                targetId: value,
                                targetName: department?.name || '',
                              },
                            })
                          }}
                          disabled={isLoadingDepartments}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder='Selecione um departamento' />
                          </SelectTrigger>
                          <SelectContent position='popper' sideOffset={5}>
                            {departmentsData?.map((department) => (
                              <SelectItem
                                key={department.id}
                                value={department.id.toString()}
                              >
                                {department.name}
                              </SelectItem>
                            )) || null}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  </div>
                )}

                {action.type === 'aplicar_tags' && (
                  <div className='space-y-1'>
                    <Label className='text-xs'>Segmentos</Label>
                    <MultiSelect
                      placeholder='Selecione os segmentos'
                      options={(tagsData || []).map((tag) => ({
                        label: tag.name,
                        value: tag.id.toString(),
                      }))}
                      onValueChange={(value) => {
                        updateAction(action.id, {
                          payload: {
                            tagIds: value,
                          },
                        })
                      }}
                      value={action.payload.tagIds || []}
                      disabled={isLoadingTags}
                    />
                    <p className='py-1 text-xs text-muted-foreground'>
                      Os segmentos selecionados serão aplicados ao contato
                      quando o agente atingir este estágio
                    </p>
                  </div>
                )}
              </>
            </Card>
          ))}
        </>
      )}
    </div>
  )
}
